import { useState } from 'react'
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

/**
 * Общие кирпичи редакторов заданий: редактируемый список строк и заголовок
 * секции.
 *
 * Восемнадцать типов различаются формой контента, но не формой ввода списков —
 * копировать одну и ту же разметку восемнадцать раз значит чинить её потом
 * восемнадцать раз.
 */

type StringListEditorProps = {
  label: string
  values: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  addLabel?: string
  minItems?: number
  multiline?: boolean
  disabled?: boolean
}

export function StringListEditor({
  label,
  values,
  onChange,
  placeholder,
  addLabel,
  minItems = 1,
  multiline = false,
  disabled = false,
}: StringListEditorProps) {
  const { t } = useTranslation()

  const update = (index: number, value: string) => {
    const next = [...values]
    next[index] = value
    onChange(next)
  }

  const remove = (index: number) => {
    onChange(values.filter((_, i) => i !== index))
  }

  return (
    <Box>
      <SectionLabel text={label} />
      <Stack spacing={1}>
        {values.map((value, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
            <TextField
              fullWidth
              size="small"
              multiline={multiline}
              minRows={multiline ? 2 : undefined}
              value={value}
              placeholder={placeholder}
              onChange={(event) => update(index, event.target.value)}
              disabled={disabled}
            />
            <IconButton
              size="small"
              onClick={() => remove(index)}
              disabled={disabled || values.length <= minItems}
              aria-label={t('common.delete')}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Box>
          <Button size="small" startIcon={<AddIcon />} onClick={() => onChange([...values, ''])} disabled={disabled}>
            {addLabel ?? t('exercises.editor.addItem')}
          </Button>
        </Box>
      </Stack>
    </Box>
  )
}

export function SectionLabel({ text, hint }: { text: string; hint?: string }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle2">{text}</Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Box>
  )
}

const parseList = (text: string): string[] =>
  text
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)

/**
 * Ввод списка через запятую — для коротких наборов вроде слов предложения.
 *
 * Пока поле в фокусе, показывается набранный текст, а не список, собранный
 * обратно через `join`. Иначе запятую нельзя было ввести вовсе: разбор сразу
 * отбрасывал пустой хвост, значение возвращалось без неё, и символ пропадал
 * в тот же кадр.
 */
export function CommaListField({
  label,
  values,
  onChange,
  helperText,
  disabled = false,
}: {
  label: string
  values: string[]
  onChange: (values: string[]) => void
  helperText?: string
  disabled?: boolean
}) {
  // null — поле не редактируют, показываем нормализованный список
  const [draft, setDraft] = useState<string | null>(null)

  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      helperText={helperText}
      value={draft ?? values.join(', ')}
      disabled={disabled}
      onFocus={() => setDraft(values.join(', '))}
      onBlur={() => setDraft(null)}
      onChange={(event) => {
        setDraft(event.target.value)
        onChange(parseList(event.target.value))
      }}
    />
  )
}
